import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import PageHeader from '@features/PageHeader';
import PageContainer from '@components/Layout/PageContainer';
import Paywall from '@features/Paywall';
import Button from '@components/Button';
import { ModalConfirm } from '@components/Modal';
import { CampaignsIcon } from 'assets/images/icons';
import { useTranslation } from '@hooks';
import {
    campaignsActions,
    campaignTemplatesActions,
    alertActions,
    channelsActions,
    surveysActions,
    labelsActions,
    couponsActions,
} from '@actions';
import { campaignsService, campaignTemplatesService } from '@services';
import { ModalType } from '@constants/modal';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import OneTimeTable from './OneTimeTable';
import Library from './Library';
import AutomationsView from './Automations';
import EditModal from './EditModal';
import { RoutePath } from '@constants/routes';
import { handleApiError } from '@helpers/api';
import usePermissions from '@hooks/usePermissions';
import { BILLING_TYPE_MAP } from '@constants/billing';
import { getPlanName } from '@helpers/billing';

const Campaigns = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const IS_LIBRARY = location.pathname === RoutePath.CampaignsLibrary;
    const IS_AUTOMATED = location.pathname === RoutePath.CampaignsAutomated;
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [modal, setModal] = useState({ open: false, type: ModalType.Create });
    const [automationsEditor, setAutomationsEditor] = useState({ open: false, id: null });

    const account = useSelector(state => state.account?.account);
    const { hasFullAccess } = usePermissions();
    const selectedCampaignRef = useRef({});
    const dispatch = useDispatch();

    const isFreemiumPlan = getPlanName(account) === BILLING_TYPE_MAP.FREEMIUM;

    useEffect(() => {
        if (isFreemiumPlan || !account?.campaignsEnabled || IS_LIBRARY) {
            return;
        }
        async function fetchData() {
            dispatch(channelsActions.get('campaigns'));
            dispatch(channelsActions.getWhatsAppBusinessTemplates());
            if (IS_AUTOMATED) {
                dispatch(surveysActions.getAll());
            } else {
                dispatch(campaignsActions.get(0, DEFAULT_PAGE_SIZE));
            }
            dispatch(campaignTemplatesActions.get());
            campaignTemplatesService
                .syncWhatsAppTemplates()
                .then(() => dispatch(campaignTemplatesActions.get()))
                .catch(() => {});
            dispatch(labelsActions.get());
            dispatch(couponsActions.getAll());
        }
        setPage(0);
        setRowsPerPage(DEFAULT_PAGE_SIZE);
        setModal({ open: false, type: ModalType.Create });
        fetchData();
    }, [dispatch, location]);

    const onClickCampaign = (campaign, type) => {
        setSelectedCampaign(campaign);
        selectedCampaignRef.current = campaign;
        switch (type) {
            case 'delete':
                setDeleteModalOpen(true);
                break;
            case 'edit':
                setModal({ open: true, type: ModalType.Edit });
                break;
            case 'view':
                setModal({ open: true, type: ModalType.View });
                break;
            default:
                break;
        }
    };

    const onDeleteCampaign = async () => {
        try {
            await campaignsService.deleteCampaign(selectedCampaignRef.current?.id);
            setDeleteModalOpen(false);
            if (modal.type !== ModalType.Create) {
                setModal({ open: false, type: ModalType.Create });
            }
            await dispatch(campaignsActions.get(page, rowsPerPage));
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
        setSelectedCampaign(null);
        selectedCampaignRef.current = {};
    };

    return (
        <PageContainer>
            {isFreemiumPlan || !account?.campaignsEnabled ? (
                <Paywall />
            ) : IS_LIBRARY ? (
                <Library />
            ) : (
                <>
                    <PageHeader
                        styles={{ marginBottom: '16px' }}
                        icon={<CampaignsIcon />}
                        title={t('CampaignsPage.title')}
                        subtitle={t('CampaignsPage.subtitle')}
                    >
                        {hasFullAccess('campaigns') && (
                            <Button
                                variant="contained"
                                onClick={() =>
                                    IS_AUTOMATED
                                        ? setAutomationsEditor({ open: true, id: null })
                                        : setModal({ open: true, type: ModalType.Create })
                                }
                            >
                                {IS_AUTOMATED ? t('AutomationsPage.create') : t('buttons.createCampaign')}
                            </Button>
                        )}
                    </PageHeader>
                    {IS_AUTOMATED ? (
                        <AutomationsView editor={automationsEditor} setEditor={setAutomationsEditor} />
                    ) : (
                        <OneTimeTable
                            rowsPerPage={rowsPerPage}
                            page={page}
                            setPage={setPage}
                            setRowsPerPage={setRowsPerPage}
                            onClickCampaign={onClickCampaign}
                        />
                    )}
                    {modal.open && !IS_AUTOMATED ? (
                        <EditModal
                            modal={modal}
                            setModal={setModal}
                            selectedCampaign={selectedCampaign}
                            setSelectedCampaign={setSelectedCampaign}
                            rowsPerPage={rowsPerPage}
                            onDelete={campaign => {
                                selectedCampaignRef.current = campaign;
                                setDeleteModalOpen(true);
                            }}
                        />
                    ) : null}
                    {isDeleteModalOpen && (
                        <ModalConfirm
                            isOpen={isDeleteModalOpen}
                            handleClose={() => setDeleteModalOpen(false)}
                            title={t('common.deleteTitle')}
                            description={t('common.deleteDescription')}
                            onPrimaryAction={onDeleteCampaign}
                            primaryActionText={t('buttons.delete')}
                            primaryActionType="danger"
                        />
                    )}
                </>
            )}
        </PageContainer>
    );
};

export default Campaigns;
